/**
 * Shared helpers for rendering a person when no photograph is stored.
 *
 * Both the speaker carousel and the member directory derived initials with their
 * own copy of the same expression and their own fallback, which meant a change
 * to how names are abbreviated had to be made twice and could silently drift.
 */

/**
 * Two-letter initials from a display name.
 *
 * `fallback` covers a name that yields no usable letters — an empty string, or
 * one made only of punctuation or whitespace — so a card never renders a blank
 * avatar.
 */
export function personInitials(name: string, fallback = "CM") {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0])
    .filter((letter): letter is string => Boolean(letter))
    .join("")
    .toUpperCase();

  return initials || fallback;
}

/**
 * A stable accent per person. Index-based rather than random so a card keeps the
 * same colour across renders, and so two people in a list do not collide
 * visually more often than the palette length requires.
 */
export const PERSON_AVATAR_COLORS = [
  "#E91E8C", "#7C3AED", "#0891B2", "#059669", "#D97706",
  "#DC2626", "#2563EB", "#9333EA", "#0D9488", "#B45309",
] as const;

export function personAvatarColor(index: number) {
  const palette = PERSON_AVATAR_COLORS;
  // Guards against a negative or fractional index producing `undefined`.
  const safeIndex = Math.abs(Math.trunc(index)) % palette.length;
  return palette[safeIndex];
}
